import {CancelIcon, ChatbotIcon} from "@components/icons";
import {api} from "@core/api";
import {ChatAction, ChatChoices} from "@core/model";
import {Selectors} from "@core/store/selectors";
import {useDownBreakpoint} from "@hooks";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Fab,
  IconButton,
  Paper,
  Portal,
  Slide,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {useThemeContext} from "@theme";
import {isAdmin, translate} from "@util";
import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {AddCommentOutlined} from "@mui/icons-material";
import {ChatMessages, UiActionState, UiMessage} from "./ChatMessages";
import {ADD_FAB_SIZE, pageFabAnchor, useScrollbarGutter} from "./fabAnchor";
import {
  ChatStreamUnsupported,
  chatErrorDescriptor,
  completionBody,
  isChatAccessError,
  streamChatMessage,
} from "./stream";
import {useTenantChatStatus} from "./useTenantChatStatus";

// Larghezza fissa: il resize non è richiesto. Stesso ordine della colonna messaggi dei ticket.
const CHAT_PANEL_WIDTH = 400;

const isAbort = (error: unknown) =>
  error instanceof Error && error.name === "AbortError";

const chatErrorText = (error: unknown) => {
  const descriptor = chatErrorDescriptor(error);
  if ("text" in descriptor) {
    return descriptor.text;
  }
  const text = translate(descriptor.key);
  if (text === descriptor.key) {
    return translate("chatbot.error.default");
  }
  return text;
};

const Chatbot = () => {
  const {theme} = useThemeContext();
  const navigate = useNavigate();
  const role = useSelector(Selectors.userRole);
  const tenantId = useSelector(Selectors.tenantId);
  // lg è lo stesso breakpoint in cui la dashboard passa al layout mobile
  const isDownLg = useDownBreakpoint("lg");
  const muiTheme = useTheme();
  const isMachine = role === "MACHINE";
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string>();
  const requestId = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const {
    currentData: status,
    error: statusError,
    enabled,
  } = useTenantChatStatus();
  const accessError = isChatAccessError(statusError);
  const [sendMessage] = api.useSendChatMessageMutation();
  const [confirmAction] = api.useConfirmChatActionMutation();
  const [cancelAction] = api.useCancelChatActionMutation();
  const canWrite =
    enabled && status?.configured === true && Boolean(status?.model?.trim());
  // Cose da fare per l'utente, solo a pannello aperto: si rileggono a ogni apertura,
  // perché un collega può assegnare un ticket nel frattempo, e dopo ogni conferma.
  const {currentData: suggestions} = api.useFetchChatSuggestionsQuery(
    tenantId ?? "",
    {skip: !open || !canWrite || !tenantId, refetchOnMountOrArgChange: true},
  );
  const scrollbarGutter = useScrollbarGutter();
  const fabAnchor = pageFabAnchor(
    muiTheme.spacing,
    isDownLg,
    open && !isDownLg ? CHAT_PANEL_WIDTH : 0,
    scrollbarGutter,
  );

  useEffect(() => {
    if (!enabled) {
      setOpen(false);
    }
  }, [enabled]);

  useEffect(() => {
    setOpen(false);
    setMessages([]);
    setError(undefined);
    requestId.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    setSending(false);
  }, [tenantId]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const stop = () => {
    requestId.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    setSending(false);
  };

  const handleNew = () => {
    stop();
    setMessages([]);
    setError(undefined);
  };

  // Azione e scelte restano sul messaggio dell'assistente che le ha proposte.
  const attachToLast = (extra: Partial<UiMessage>) =>
    setMessages(current => {
      const next = [...current];
      const last = next[next.length - 1];
      if (last?.role === "assistant") {
        next[next.length - 1] = {...last, ...extra};
        return next;
      }
      return [
        ...next,
        {role: "assistant", content: "", createdAt: Date.now(), ...extra},
      ];
    });

  // Una nuova proposta sostituisce quelle ancora in attesa: il backend le ha già scartate.
  const supersedePending = () =>
    setMessages(current =>
      current.map(message =>
        message.actionState === "pending"
          ? {...message, actionState: "superseded"}
          : message,
      ),
    );

  const attachAction = (action: ChatAction) => {
    supersedePending();
    attachToLast({action, actionState: "pending"});
  };

  const attachChoices = (choices: ChatChoices) => attachToLast({choices});

  const setActionState = (id: string, actionState: UiActionState) =>
    setMessages(current =>
      current.map(message =>
        message.action?.id === id ? {...message, actionState} : message,
      ),
    );

  // L'esito entra nella conversazione come messaggio dell'assistente,
  // così al turno dopo il modello sa cosa è stato fatto.
  const addAssistantMessage = (content: string) =>
    setMessages(current => [
      ...current,
      {role: "assistant", content, createdAt: Date.now()},
    ]);

  const handleConfirm = async (actionId: string) => {
    setActionState(actionId, "running");
    setError(undefined);
    try {
      const outcome = await confirmAction(actionId).unwrap();
      setActionState(actionId, outcome.done ? "done" : "failed");
      addAssistantMessage(outcome.message);
    } catch (confirmError) {
      setActionState(actionId, "failed");
      setError(chatErrorText(confirmError));
    }
  };

  const handleCancel = (action: ChatAction) => {
    setActionState(action.id, "cancelled");
    addAssistantMessage(
      `${translate("chatbot.action.cancelledMessage")}: ${action.summary}`,
    );
    // se non arriva al backend, l'azione scade da sola
    cancelAction(action.id)
      .unwrap()
      .catch(() => undefined);
  };

  const handleSend = async (text: string) => {
    if (!canWrite) {
      return;
    }
    const id = requestId.current + 1;
    requestId.current = id;
    const history: UiMessage[] = [
      ...messages,
      {role: "user", content: text, createdAt: Date.now()},
    ];
    setMessages(history);
    setError(undefined);
    setSending(true);

    const request = completionBody({
      messages: history.map(({role, content}) => ({role, content})),
    });
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamChatMessage(
        request,
        delta => {
          if (requestId.current !== id) {
            return;
          }
          setMessages(current => {
            const next = [...current];
            const last = next[next.length - 1];
            if (last?.role === "assistant") {
              next[next.length - 1] = {
                ...last,
                content: last.content + delta,
              };
              return next;
            }
            return [
              ...next,
              {role: "assistant", content: delta, createdAt: Date.now()},
            ];
          });
        },
        controller.signal,
        action => {
          if (requestId.current === id) {
            attachAction(action);
          }
        },
        choices => {
          if (requestId.current === id) {
            attachChoices(choices);
          }
        },
      );
    } catch (streamError) {
      if (requestId.current !== id || isAbort(streamError)) {
        return;
      }
      // lo stream è opzionale: 404/405 usano la POST completa
      if (streamError instanceof ChatStreamUnsupported) {
        try {
          const completion = await sendMessage(request).unwrap();
          if (requestId.current !== id) {
            return;
          }
          if (completion.action) {
            supersedePending();
          }
          setMessages(current => [
            ...current,
            {
              role: "assistant",
              content: completion.content,
              createdAt: Date.now(),
              ...(completion.action
                ? {action: completion.action, actionState: "pending" as const}
                : {}),
              ...(completion.choices ? {choices: completion.choices} : {}),
            },
          ]);
        } catch (mutationError) {
          if (requestId.current !== id) {
            return;
          }
          setError(chatErrorText(mutationError));
        }
      } else {
        setError(chatErrorText(streamError));
      }
    } finally {
      if (requestId.current === id) {
        setSending(false);
        abortRef.current = null;
      }
    }
  };

  const openSettings = () => {
    setOpen(false);
    navigate("/dashboard/admin");
  };

  const panel = (
    <Stack flex={1} minHeight={0} width="100%">
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        px={2}
        py={1.5}
        borderBottom={1}
        borderColor="divider">
        <Typography variant="h6" component="h2" flex={1} noWrap>
          {translate("chatbot.title")}
        </Typography>
        {canWrite && (
          <IconButton
            aria-label={translate("chatbot.new")}
            onClick={handleNew}
            size="small">
            <AddCommentOutlined />
          </IconButton>
        )}
        <Tooltip title={translate("chatbot.close")}>
          <IconButton
            aria-label={translate("chatbot.close")}
            onClick={() => setOpen(false)}
            size="small">
            <CancelIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {canWrite && error && (
        <Alert severity="error" sx={{mx: 2, mt: 1}}>
          {error}
        </Alert>
      )}

      {accessError && (
        <Stack flex={1} px={2} py={3} justifyContent="center">
          <Typography>{chatErrorText(statusError)}</Typography>
        </Stack>
      )}

      {enabled && !accessError && !canWrite && (
        <Stack flex={1} px={2} py={3} gap={2} justifyContent="center">
          <Typography>
            {translate(
              role && isAdmin(role)
                ? "chatbot.notConfiguredAdmin"
                : "chatbot.notConfigured",
            )}
          </Typography>
          {role && isAdmin(role) && (
            <Button variant="contained" onClick={openSettings}>
              {translate("chatbot.configure")}
            </Button>
          )}
        </Stack>
      )}

      {canWrite && (
        <Stack flex={1} minHeight={0} px={2} pb={1} pt={1}>
          <ChatMessages
            messages={messages}
            sending={sending}
            suggestions={suggestions}
            onSend={handleSend}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
          />
        </Stack>
      )}
    </Stack>
  );

  if (isMachine || !enabled) {
    return null;
  }

  const fab = (
    <Box
      sx={{
        position: "fixed",
        right: fabAnchor.right,
        bottom: fabAnchor.bottom,
        zIndex: muiTheme => muiTheme.zIndex.fab,
        height: ADD_FAB_SIZE,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        transition: muiTheme =>
          muiTheme.transitions.create(["right", "width"], {
            easing: muiTheme.transitions.easing.sharp,
            duration: muiTheme.transitions.duration.enteringScreen,
          }),
      }}
      style={{width: open ? 0 : ADD_FAB_SIZE}}>
      {!open && (
        // Il contenitore taglia ai bordi per l'animazione: l'ombra del Fab verrebbe squadrata.
        <Fab
          color="primary"
          aria-label={translate("chatbot.open")}
          onClick={() => setOpen(true)}
          sx={{boxShadow: "none", "&:active": {boxShadow: "none"}}}>
          <ChatbotIcon />
        </Fab>
      )}
    </Box>
  );

  // Sotto lg la dashboard è già a colonna: il pannello copre tutta la larghezza.
  if (isDownLg) {
    return (
      <>
        <Portal>
          <Slide direction="left" in={open} mountOnEnter unmountOnExit>
            <Paper
              role="dialog"
              aria-label={translate("chatbot.title")}
              square
              elevation={8}
              sx={{
                position: "fixed",
                top: 0,
                right: 0,
                height: "100%",
                width: "100%",
                zIndex: muiTheme => muiTheme.zIndex.drawer,
                display: "flex",
              }}>
              {panel}
            </Paper>
          </Slide>
        </Portal>
        <Portal>{fab}</Portal>
      </>
    );
  }

  return (
    <>
      <Collapse
        in={open}
        orientation="horizontal"
        mountOnEnter
        unmountOnExit
        sx={{flexShrink: 0, alignSelf: "stretch", height: "100%"}}>
        <Paper
          role="dialog"
          aria-label={translate("chatbot.title")}
          square
          elevation={0}
          sx={{
            width: CHAT_PANEL_WIDTH,
            height: "100%",
            display: "flex",
            borderLeft: `2px solid ${theme.colors.secondaryContainer}`,
          }}>
          {panel}
        </Paper>
      </Collapse>
      <Portal>{fab}</Portal>
    </>
  );
};

export {Chatbot};
